import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
} from '@mui/material';
import link from 'packages/novous_web/src/assets/icons/Link.svg'
import InputField from './InputField';
import CustomIconButton from './CustomIconButton';
import { API_ENDPOINTS } from '../../api/endpoints';
import useAxios from '../../api/useAxios';
import AlertToast from './Alert';
import useApiError from '../../api/useApiError';

interface ShareSosFootageModalProps {
  open: boolean;
  onClose: () => void;
  heading?: string;
  shareLink: any;
  subheading?: string;
  isDriverCoachingVideos?: boolean;
  videoLinkDriverCoaching?: any;
  videoPlayback?: boolean
  tripInfo?: any
}

const ShareSosFootageModal: React.FC<ShareSosFootageModalProps> = ({
  open,
  onClose,
  heading = 'Share Footage',
  subheading = 'Enter an email address to share this Footage',
  shareLink,
  isDriverCoachingVideos,
  videoLinkDriverCoaching,
  videoPlayback,
  tripInfo
}) => {
  const [email, setEmail] = useState('');
  const [userEmailValid, setUserEmailValid] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", type: "error" });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const {
    data: shareTripData,
    error: shareTripError,
    loading: shareTripLoading,
    fetchData: fetchShareTripData,
  } = useApiError<any>(`${API_ENDPOINTS.alarm.SHARE_ALERT_DETAILS_EMAIL}`, 'POST');

  const {
    data: shareVideoPlaybackData,
    error: shareVideoPlaybackError,
    loading: shareVideoPlaybackLoading,
    fetchData: fetchShareVideoPlaybackData,
  } = useApiError<any>(`${API_ENDPOINTS.shareLink.VIDEO_PLAYBACK_SHARE}`, 'POST');

  const {
    data: shareDriverCoachingData,
    error: shareDriverCoachingError,
    loading: shareDriverCoachingLoading,
    fetchData: fetchShareDriverCoachingData,
  } = useApiError<any>(`${API_ENDPOINTS.alarm.DRIVER_COACHING_SHARE_LINK}`, 'POST');

  const validateEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  useEffect(() => {
    setUserEmailValid(validateEmail(email));
  }, [email]);

  const handleSubmit = async () => {
    if (!userEmailValid) {
      alert('Please enter a valid email address');
      return;
    }
    setIsSendingEmail(true);
    try {
      let response;

      if (isDriverCoachingVideos) {
        const videoLinks = videoLinkDriverCoaching.map(
          (video: any) => video.playBackUrl
        );
        // Call driver coaching API
        response = await fetchShareDriverCoachingData({
          emailId: email,
          videoLink: videoLinks
        });
      } else if (videoPlayback) {
        // Call video playback API
        response = await fetchShareVideoPlaybackData({
          emailId: email,
          deviceId: tripInfo.deviceId,
          sortKey: tripInfo?.sortkey,
        });
      } else {
        // Call regular trip footage API
        response = await fetchShareTripData({
          emailId: email,
          deviceId: shareLink.deviceId,
          warningSortKey: shareLink.warningSortKey
        });
      }

      if (response?.status === 201 || response?.status === 200) {
        setToast({
          open: true,
          message: response?.data || 'Footage shared successfully',
          type: 'success'
        });
        setEmail('');
        onClose();
      } else {
        setToast({
          open: true,
          message: response?.error || 'Failed to share footage',
          type: 'error'
        });
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setToast({
        open: true,
        message: 'An error occurred while sharing footage',
        type: 'error'
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // useEffect(() => {
  //   if (shareTripData && shareTripData.message == 'Footage Details Shared Successfully') {
  //     setToast({ open: true, message: 'Footage Details Shared Successfully', type: 'success' })
  //     setTimeout(() => {
  //       onClose();
  //     }, 3000)
  //   }
  // }, [shareTripData])

  const handleClose = () => {
    if (shareTripLoading) return; // Prevent closing during loading
    setEmail('');
    onClose();
  };

  return (
    <>
      <AlertToast open={toast.open} message={toast.message} onClose={() => setToast({ ...toast, open: false })} type={toast.type as any} />
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Typography variant="h6">{heading}</Typography>
            <CustomIconButton
              text={`Copy Link`}
              icon={<img src={link} alt='link' width={24} height={24} />}
              background="transparent"
              outline={false}
              padding="0px"
              size={16}
              variant="outlined"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href)
                  .then(() => {
                    console.log('Link copied to clipboard!');
                  })
                  .catch(err => {
                    console.error('Failed to copy link: ', err);
                  });
              }} />
          </Box>
          <Typography variant="button" fontWeight={400} className='mb-1' color="secondary">
            {subheading}
          </Typography>
          <div className="mt-4">
            <InputField
              label="Email Address"
              name="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="Enter email address"
              status={email.length === 0 ? undefined : userEmailValid ? 'success' : 'error'}
              isDisabled={false} />
          </div>
          <div className='flex w-full mt-8 justify-between'>
            <CustomIconButton
              text={`Cancel`}
              background="transparent"
              outline={true}
              padding="0px"
              size={16}
              variant="outlined"
              onClick={handleClose} />
            <CustomIconButton
              text={isSendingEmail ? 'Sending...' : `Send`}
              padding="0px"
              outline={false}
              size={16}
              isDisabled={!userEmailValid || isSendingEmail}
              variant="contained"
              onClick={handleSubmit} />
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default ShareSosFootageModal;
